import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  useCheckLoginMutation,
  useCreateOtpMutation,
  useVerifyOtpMutation,
} from "../redux/crmAdminApiSlice";
import {
  setFormData,
  setOtpDigit,
  setOtp,
  setShowOtpPopup,
  setError,
  setLoading,
  setOtpSent,
  resetAuthState,
  loginSuccess,
  logoutSuccess,
} from "../redux/crmAdminAuthSlice";
import "../css/AdminCRMLogin.css";

const AdminCRMLogin = () => {
  const dispatch = useDispatch();
  const otpInputs = useRef([]);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/crm");
    }
  }, [isAuthenticated, navigate]);

  // Select state from Redux store
  const {
    countryCode,
    phoneNumber,
    password,
    otp,
    showOtpPopup,
    error,
    loading,
    otpSent,
  } = useSelector((state) => state.auth);

  // RTK Query mutations
  const [checkLogin] = useCheckLoginMutation();
  const [createOtp] = useCreateOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    dispatch(setError(""));
    dispatch(setLoading(true));

    // Ensure phoneNumber is set before making API call
    if (!phoneNumber) {
      dispatch(setError("Please enter a valid phone number."));
      dispatch(setLoading(false));
      return;
    }

    try {
      const response = await checkLogin({
        countryCode,
        phoneNumber,
        password,
      }).unwrap();

      if (response.success) {
        await handleCreateOtp(); // Request OTP if login succeeds
      } else {
        dispatch(setError("Invalid phone number or password"));
      }
    } catch (err) {
      dispatch(setError(err.data?.message || "Authentication failed"));
      if (err.status === 401) {
        dispatch(setFormData({ name: "password", value: "" }));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Create and send OTP to user's phone
  const handleCreateOtp = async () => {
    dispatch(setLoading(true));

    if (!phoneNumber) {
      dispatch(
        setError("Please enter your phone number before requesting OTP."),
      );
      dispatch(setLoading(false));
      return;
    }

    try {
      const response = await createOtp({
        countryCode,
        phoneNumber,
      }).unwrap();

      if (response.success) {
        dispatch(setShowOtpPopup(true));
        dispatch(setOtpSent(true));
        dispatch(setError(""));
        setTimeout(() => otpInputs.current[0]?.focus(), 100);
      } else {
        dispatch(setError("Failed to send OTP. Please try again."));
      }
    } catch (err) {
      dispatch(setError("Failed to send OTP. Please try again later."));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Verify the entered OTP
  const handleVerifyOtp = async () => {
    const otpDigits = otp.filter((d) => d !== ""); // Remove empty values

    if (otpDigits.length !== 4) {
      dispatch(setError("Please enter the complete 4-digit code"));
      return;
    }

    if (!phoneNumber) {
      dispatch(
        setError("Phone number is missing. Please restart the login process."),
      );
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await verifyOtp({
        countryCode,
        phone: phoneNumber,
        otp: otpDigits, // Ensure the OTP is sent as an array
      }).unwrap();

      console.log("Verify OTP Response:", response); // Debugging

      if (response.success) {
        dispatch(loginSuccess({ user: response.user }));
        navigate("/crm"); // Redirect after successful OTP verification
      } else {
        dispatch(setError("Invalid verification code. Please try again."));
        dispatch(setOtp(["", "", "", ""]));
        setTimeout(() => otpInputs.current[0]?.focus(), 100);
      }
    } catch (err) {
      console.error("Verify OTP Error:", err);
      dispatch(setError("Verification failed. Please try again."));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow numbers

    dispatch(setOtpDigit({ index, value }));
    dispatch(setError(""));

    // Auto-focus next input if a digit was entered
    if (value && index < 3) {
      otpInputs.current[index + 1].focus();
    }
  };

  // Handle backspace key in OTP inputs
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs.current[index - 1].focus();
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    dispatch(setFormData({ name: e.target.name, value: e.target.value }));
  };

  return (
    <div className="login-container">
      {/* Login Form */}
      <div className="login-box">
        <h2 className="login-title">Admin CRM Login</h2>
        {error && !showOtpPopup && <p className="error-message">{error}</p>}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="countryCode">Country Code</label>
            <input
              type="text"
              id="countryCode"
              name="countryCode"
              value={countryCode}
              onChange={handleChange}
              placeholder="+1"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={phoneNumber}
              onChange={handleChange}
              placeholder="Phone number"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handleChange}
              placeholder="Password"
              required
            />
          </div>

          <button
            className="login-button"
            disabled={loading}
            aria-busy={loading}
            aria-live="polite"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              "Login"
            )}
          </button>
        </form>
      </div>

      {/* OTP Verification Popup */}
      {showOtpPopup && (
        <div className="otp-popup-overlay">
          <div className="otp-popup">
            <h3 className="otp-title">Enter Verification Code</h3>
            <p className="otp-instructions">
              {otpSent
                ? "We've sent a 4-digit code to your phone"
                : "Sending code to your phone..."}
            </p>

            <div className="otp-input-container">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  ref={(el) => (otpInputs.current[index] = el)}
                  maxLength={1}
                  className={`otp-digit ${error ? "error" : ""}`}
                  disabled={!otpSent}
                />
              ))}
            </div>

            {error && <p className="otp-error-message">{error}</p>}

            <div className="otp-button-group">
              <button
                onClick={handleVerifyOtp}
                className="verify-button"
                disabled={loading || !otpSent}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>

              <button
                onClick={() => {
                  dispatch(setShowOtpPopup(false));
                  dispatch(setOtp(["", "", "", ""]));
                  dispatch(setError(""));
                }}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>

            <p className="resend-otp-text">
              Didn't receive code?{" "}
              <button
                onClick={handleCreateOtp}
                className="resend-button"
                disabled={loading}
              >
                Resend OTP
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCRMLogin;
